import type { TableColumnIdentifier } from '@tempo-io/roadmap-dto';
import type { CustomField, CustomFieldTypes } from '@/lib/api/types/customField';
import type { ItemStatus } from '@/lib/api/types/itemStatus';
import type { ItemLinkValue, ItemMultiLinkValue } from '@/lib/api/types/items';
import type { ItemTypeMap } from '@/lib/api/types/itemType';
import type { PickListOption } from '@/lib/api/types/picklistOption';
import type { Team } from '@/lib/api/types/teams';
import { parseDateValue, toISODateString } from '@/lib/utils/common/dateFormats';
import { isFiniteNumber } from '@/lib/utils/common/number';
import type { ItemsTableRow } from '../types';

const MULTI_FIELD_TYPES: ReadonlySet<CustomFieldTypes> = new Set([
  'MULTI_PICKLIST',
  'MULTI_ITEM',
  'MULTI_USER',
  'MULTI_CUSTOMER',
  'MULTI_TEXT',
  'MULTI_LINK',
]);

const htmlToPlainText = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
};

const getNumericFieldValue = (value: unknown) => (isFiniteNumber(value) ? value : undefined);
const getDateValue = (value: unknown) => {
  if (!value || (typeof value !== 'string' && !(value instanceof Date))) return undefined;
  return parseDateValue(value);
};
const getUserIdValue = (idValue: unknown): string | undefined =>
  idValue ? String(idValue) : undefined;

export const getCellValue = (
  item: ItemsTableRow,
  columnIdentifier: TableColumnIdentifier,
  metadata: {
    customFieldById: Map<CustomField['id'], CustomField>;
    itemStatusById: Map<ItemStatus['id'], ItemStatus>;
    itemTypesById: ItemTypeMap;
    teamsById: Record<Team['id'], Team>;
    getCustomFieldPickListOptionById: (
      customField: CustomField,
      pickListOptionId?: PickListOption['id'],
    ) => PickListOption | undefined;
    estimationDataById: Record<number, { schemeName: string; label: string }>;
  },
) => {
  const {
    customFieldById,
    itemStatusById,
    itemTypesById,
    teamsById,
    getCustomFieldPickListOptionById,
    estimationDataById,
  } = metadata;

  if (columnIdentifier.type === 'customField') {
    if (!columnIdentifier.customFieldId) return undefined;

    const customField = customFieldById.get(columnIdentifier.customFieldId);
    if (!customField || columnIdentifier.customFieldId !== customField.id) return undefined;

    const entry = item.customFieldValues?.[String(customField.id)];
    const isMultiType = MULTI_FIELD_TYPES.has(customField.type);
    const entryIsArray = Array.isArray(entry);

    if (entry != null && isMultiType !== entryIsArray) {
      return isMultiType ? [] : undefined;
    }

    const single = entryIsArray ? undefined : (entry ?? undefined);
    const multi = entryIsArray ? entry : undefined;
    const singleValue = single?.value;
    const multiValues = multi ? multi.map((dto) => dto.value) : undefined;

    const isNonEmptyString = (v: unknown): v is string => typeof v === 'string' && v.length > 0;

    switch (customField.type) {
      case 'CURRENCY':
      case 'NUMERIC':
        return getNumericFieldValue(singleValue);

      case 'DATE':
        return getDateValue(singleValue);

      case 'TEXT':
      case 'MULTILINE_TEXT':
        return typeof singleValue === 'string' ? singleValue : undefined;

      case 'NOTE':
        return typeof singleValue === 'string' ? htmlToPlainText(singleValue) : undefined;

      case 'USER':
        return getUserIdValue(singleValue);

      case 'CHECKBOX':
        return typeof singleValue === 'boolean' ? singleValue : undefined;

      case 'ITEM':
        return isFiniteNumber(singleValue) ? singleValue : undefined;

      case 'MULTI_TEXT':
      case 'MULTI_USER':
        return multiValues?.filter(isNonEmptyString) ?? [];

      case 'MULTI_ITEM':
      case 'MULTI_CUSTOMER':
        return multiValues?.filter(isFiniteNumber) ?? [];

      case 'PICKLIST':
        return getCustomFieldPickListOptionById(
          customField,
          typeof singleValue === 'number' ? singleValue : undefined,
        );

      case 'MULTI_PICKLIST':
        return (
          multiValues
            ?.filter((v): v is number => typeof v === 'number')
            .map((optionId) => getCustomFieldPickListOptionById(customField, optionId))
            .filter(Boolean) ?? []
        );

      case 'LINK': {
        if (typeof singleValue !== 'string') return undefined;
        const link: ItemLinkValue = { LINK: singleValue };
        if (single?.description !== undefined) link.description = single.description;
        return link;
      }

      case 'MULTI_LINK':
        return (
          multi
            ?.filter((dto) => isNonEmptyString(dto.value))
            .map(
              (dto) =>
                ({
                  MULTI_LINK: dto.value as string,
                  ...(dto.description !== undefined && { description: dto.description }),
                }) as ItemMultiLinkValue,
            ) ?? []
        );

      default:
        return entry != null ? JSON.stringify(entry) : undefined;
    }
  }

  // Map system fields to item properties
  switch (columnIdentifier.type) {
    case 'id':
      return item.id;

    case 'externalKey':
      return item.externalLink
        ? { LINK: item.externalLink, description: item.externalKey }
        : undefined;

    case 'createdAt':
    case 'updatedAt':
      return getDateValue(item[columnIdentifier.type]);

    case 'timeSpent':
      return item.timeSpentSeconds;

    case 'earliestStartDate':
      return getDateValue(item.earliestStartDateTime);

    case 'deadlineDate':
      return getDateValue(item.deadlineDateTime);

    case 'plannedStartDate':
      return getDateValue(toISODateString(item.plannedStartDateTime));

    case 'plannedFinishDate':
      return getDateValue(toISODateString(item.plannedFinishDateTime));

    case 'doneDate':
      return getDateValue(item.doneDateTime);

    case 'createdBy':
    case 'updatedBy':
      return getUserIdValue(item[columnIdentifier.type]);

    case 'status':
      return item.itemStatusId ? itemStatusById.get(item.itemStatusId) : undefined;

    case 'itemType':
      return item.itemTypeId ? (itemTypesById[item.itemTypeId]?.name ?? '') : '';

    case 'team':
      return item.teamId ? teamsById[item.teamId]?.name || '' : '';

    case 'estimationScheme':
      return item.estimationId ? (estimationDataById[item.estimationId]?.schemeName ?? '') : '';

    case 'estimation':
      return item.estimationId ? (estimationDataById[item.estimationId]?.label ?? '') : '';

    // Schedule fields are loaded separately via ScheduleBatchLoadingContext, not from the item row
    case 'expectedStartDate':
    case 'expectedFinishDate':
    case 'latestFinishDate':
      return undefined;

    // Health fields are loaded asynchronously via VisibleHealthContext, not from the item row
    case 'health':
    case 'timeSpentVsPlanned':
      return undefined;

    // Integration data is loaded asynchronously via VisibleIntegrationsContext, not from the item row
    case 'integration':
      return undefined;

    default:
      return undefined;
  }
};
